import React, { useState } from 'react';
import {
  Box, Typography, IconButton, Dialog, Tooltip, Paper
} from '@mui/material';
import {
  Download as DownloadIcon,
  Description as DocIcon,
  Image as ImageIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  AttachFile as FileIcon
} from '@mui/icons-material';

/**
 * Composant pour afficher une pièce jointe de manière attrayante (style WhatsApp)
 */
const AttachmentBubble = ({ attachment, isFromUser }) => {
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  
  // Déterminer le type de fichier
  const getFileType = (mimetype = '', filename = '') => {
    if (mimetype.startsWith('image/')) return 'image';
    if (mimetype === 'application/pdf' || filename?.endsWith('.pdf')) return 'pdf';
    return 'document';
  };

  // Obtenir l'icône appropriée
  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf':
        return <PdfIcon sx={{ fontSize: 32 }} />;
      case 'image':
        return <ImageIcon sx={{ fontSize: 32 }} />;
      case 'document':
        return <DocIcon sx={{ fontSize: 32 }} />;
      default:
        return <FileIcon sx={{ fontSize: 32 }} />;
    }
  };

  // Formater la taille du fichier
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const fileType = getFileType(attachment.mimetype, attachment.originalname || attachment.filename);
  const fileName = attachment.originalname || attachment.filename || 'File';
  const fileSize = formatFileSize(attachment.size);
  // Utiliser l'URL absolue du serveur si disponible, sinon construire relativement
  const fileUrl = attachment.url;
  const isImage = fileType === 'image';

  if (!fileUrl) {
    return (
      <Tooltip title="Lien invalide">
        <Paper
          sx={{
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            bgcolor: '#fff3e0',
            borderRadius: 2,
            border: '1px solid',
            borderColor: '#ffb74d',
            maxWidth: 240,
            cursor: 'not-allowed',
            opacity: 0.7
          }}
        >
          <ErrorIcon sx={{ fontSize: 32, color: '#f57c00', flexShrink: 0 }} />
          <Box>
            <Typography variant="caption" fontWeight={600} sx={{ display: 'block', color: '#e65100' }}>
              Fichier indisponible
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {fileName.substring(0, 20)}...
            </Typography>
          </Box>
        </Paper>
      </Tooltip>
    );
  }

  return (
    <>
      <Paper
        component="a"
        href={fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        download={fileName}
        sx={{
          p: 1,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          maxWidth: 240,
          cursor: 'pointer',
          textDecoration: 'none',
          color: 'inherit',
          background: isFromUser 
            ? 'linear-gradient(135deg, #1d41e3 0%, #780be6 100%)'
            : 'linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)',
          borderRadius: isFromUser ? '18px 4px 18px 18px' : '4px 18px 18px 18px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          border: isFromUser ? 'none' : '1px solid',
          borderColor: isFromUser ? 'transparent' : 'primary.light',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-2px)',
          }
        }}
      >
        {/* Icône fichier ou aperçu image */}
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {isImage ? (
            <Box
              onClick={(e) => {
                e.preventDefault();
                setImagePreviewOpen(true);
              }}
              sx={{
                width: 48,
                height: 48,
                borderRadius: '8px',
                overflow: 'hidden',
                cursor: 'pointer',
                border: '2px solid',
                borderColor: isFromUser ? 'rgba(255,255,255,0.3)' : 'primary.light',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: isFromUser ? 'rgba(255,255,255,0.6)' : 'primary.main',
                  transform: 'scale(1.05)'
                }
              }}
            >
              <img
                src={fileUrl}
                alt={fileName}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                color: isFromUser ? 'white' : 'primary.main',
                opacity: isFromUser ? 0.9 : 1
              }}
            >
              {getFileIcon(fileType)}
            </Box>
          )}
        </Box>

        {/* Informations du fichier */}
        <Box sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
          <Typography
            variant="caption"
            fontWeight={600}
            sx={{
              display: 'block',
              wordBreak: 'break-word',
              color: isFromUser ? 'white' : 'text.primary',
              fontSize: '0.82rem',
              lineHeight: 1.3,
              mb: 0.25
            }}
          >
            {fileName.length > 24 ? fileName.substring(0, 21) + '...' : fileName}
          </Typography>
          {fileSize && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: isFromUser ? 'rgba(255,255,255,0.75)' : 'text.secondary',
                fontSize: '0.7rem'
              }}
            >
              {fileSize}
            </Typography>
          )}
        </Box>

        {/* Icône téléchargement */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.preventDefault();
            const link = document.createElement('a');
            link.href = fileUrl;
            link.download = fileName;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
          sx={{
            flexShrink: 0,
            p: '6px',
            color: isFromUser ? 'white' : 'primary.main',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: isFromUser ? 'rgba(255,255,255,0.2)' : 'primary.lighter',
              transform: 'scale(1.1)'
            }
          }}
        >
          <DownloadIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Paper>

      {/* Dialog pour aperçu d'image */}
      {isImage && (
        <Dialog
          open={imagePreviewOpen}
          onClose={() => setImagePreviewOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              backgroundImage: 'none'
            }
          }}
        >
          <Box sx={{ position: 'relative', bgcolor: '#000' }}>
            <IconButton
              onClick={() => setImagePreviewOpen(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                zIndex: 1,
                transition: 'all 0.2s ease',
                '&:hover': { 
                  bgcolor: 'rgba(0,0,0,0.7)',
                  transform: 'scale(1.1)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={fileUrl}
              alt={fileName}
              style={{
                width: '100%',
                display: 'block',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Box>
        </Dialog>
      )}
    </>
  );
};

export default AttachmentBubble;
